// src/ui/screens/capture.ts
// CAPTURE — WHICH IS A FILE PICKER, AND DELIBERATELY NOTHING MORE.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THERE IS NO BROWSER CAMERA IN THIS APP. THAT IS THE ARCHITECTURE, NOT A SHORTCUT.
// ══════════════════════════════════════════════════════════════════════════════════════════════
// The athlete films in the phone's OWN camera app — the normal iOS or Android camera — and then
// picks that original file here. No `getUserMedia`, no `MediaRecorder`, no canvas, no re-encode.
//
// The reason is specific: browser camera APIs cannot yet be trusted to deliver the frame rate and
// the capture control the metrics need, and the stadium footage has not yet established what those
// requirements really are. Recording through the browser would mean shipping a capture path whose
// fps we cannot guarantee, and then discovering after the footage lands that every clip recorded
// through it is unusable. The native camera is the one recorder on the device that is known good.
//
// Two consequences this file is careful about, because both are ways to silently undo that decision:
//
//   1. `capture="environment"` IS NOT SET on the input. That attribute tells the phone to open its
//      camera directly and hand back a NEW recording made through the picker — on some Android
//      builds a lower-quality one, and with the browser deciding the encode. The whole point is to
//      take the file the camera app ALREADY wrote. So the input offers the library, not a recorder.
//   2. The `File` is passed to the queue and to the uploader UNTOUCHED. It is never read into a
//      canvas, never transcoded, never trimmed, never resized. Whatever the camera wrote is what
//      lands in R2, byte for byte — which is what makes the eventual analysis argue with the camera
//      rather than with us.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// AND: THIS ENTIRE SCREEN IS DARK TODAY
// ══════════════════════════════════════════════════════════════════════════════════════════════
// R2 is not configured in production — both bucket variables are null — so `initiateUpload` answers
// 503 with `reason: r2_not_configured` and the list of missing variables. That refusal is rendered
// as itself: "Video upload is not enabled yet", the server's own sentence, and the variables a
// person must set. It is NOT a spinner, NOT a retry loop, and NOT a fake success. The file stays in
// the queue in the `held` state until someone configures storage, and the rest of the app works
// completely without it.

import { el, empty, field, mount, section, why } from '../dom';
import { refusalView } from '../refusal-view';
import { getState } from '../state';
import { enqueueUpload, drain, listJobs, type UploadJob } from '../../queue/queue';
import { SUPPORTED_CONTENT_TYPES, contentTypeFor } from '../../upload/multipart';
import { uploadStatus, getSession, CAMERA_ROLES, type CameraRole } from '../../api/practice';
import { isRefusal } from '../../api/client';
import { classify } from '../../api/refusal';

const MB = 1024 * 1024;
const fmt = (bytes: number): string =>
  bytes >= MB ? `${(bytes / MB).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;

export async function captureScreen(root: HTMLElement, rerender: () => void): Promise<void> {
  const st = getState();
  const out = el('div');

  if (!st.athlete) {
    mount(root, section('Upload a clip',
      empty('No athlete selected. Choose one from the roster on the Coach screen first — an athlete '
        + 'id is only ever used here after the server handed it to us.')));
    return;
  }
  const athlete = st.athlete;

  const status = el('div');

  // NOTE: no `capture` attribute. See the header block — this opens the library, not a recorder.
  const picker = el('input', {
    class: 'input',
    type: 'file',
    accept: 'video/*',
  });

  const chosen = el('div');

  // ── CAMERA ROLE — REQUIRED WHENEVER A SESSION IS BOUND (fixed 26 Aug 2026) ──────────────────
  // This screen used to pass `cameraRole: null` as a literal while passing `sessionId` from state.
  // The server requires the two TOGETHER ("camera_role is required alongside session_id and must be
  // one of the 041 vocabulary"), so every upload with a session attached was guaranteed to be
  // refused — and refused at `complete`, which is AFTER the whole file has been pushed to storage.
  // The founder's queue showed a 1.9 MB clip that had uploaded fully, been rejected, and left an
  // object behind. Twenty attempts of that.
  //
  // The role is not invented here. It is READ BACK from the session the coach already created, and
  // the picker is seeded with it — this app has no vocabulary of its own for a camera.
  const roleSelect = el('select', { class: 'input' },
    ...CAMERA_ROLES.map((r) => el('option', { value: r }, r))) as HTMLSelectElement;
  let roleKnown = false;

  if (st.sessionId) {
    const s = await getSession(st.sessionId);
    if (!isRefusal(s) && s.value.session?.camera_role) {
      roleSelect.value = s.value.session.camera_role;
      roleKnown = true;
    }
  }

  picker.addEventListener('change', () => {
    const file = picker.files?.[0];
    if (!file) { mount(chosen); return; }
    const ct = contentTypeFor(file);
    mount(chosen,
      field('File', file.name),
      field('Size', fmt(file.size)),
      field('Browser reports type', file.type || '(none)'),
      field('Will be sent as', ct),
      file.type && !(SUPPORTED_CONTENT_TYPES as readonly string[]).includes(file.type.split(';')[0] ?? '')
        && el('p', { class: 'note' },
          `The browser reported "${file.type}", which is not on the server’s accepted list, so this `
          + 'will be sent as application/octet-stream — which the server does accept. The file '
          + 'itself is not modified.'),
      el('button', {
        class: 'btn btn-primary',
        onclick: () => {
          void (async () => {
            const job = await enqueueUpload({
              athleteId: athlete.athleteId,
              file,
              sessionId: st.sessionId,
              // Only meaningful alongside a session; the server rejects the pair being half-present.
              cameraRole: st.sessionId ? (roleSelect.value as CameraRole) : null,
            });
            mount(status, el('p', { class: 'note' },
              `Queued as ${job.id}. Idempotency key: ${job.client_upload_uuid}. `
              + 'This key is identical on every retry, which is what lets a killed upload resume '
              + 'instead of starting over.'));
            await drain();
            rerender();
          })();
        },
      }, 'Queue this upload'),
    );
  });

  out.append(section('Record with the phone’s own camera',
    why('Film in the normal iOS or Android camera app, then pick that original file here. This app '
      + 'deliberately does not open a browser camera: browser capture APIs cannot yet be trusted to '
      + 'give the frame rate and control the metrics will need, and the footage that would settle '
      + 'what those requirements are does not exist yet.'),
    el('p', { class: 'note' },
      'The file is uploaded exactly as the camera wrote it — never transcoded, trimmed or resized.'),
    !st.sessionId && el('p', { class: 'warn' },
      'No session is bound. The upload will still store the video and stay reachable by consent '
      + 'revocation, but the server will not write a practice clip row and will say so '
      + '(clip_reason: no_session_id).'),
    st.sessionId && el('label', { class: 'label' }, 'Camera role for this clip'),
    st.sessionId && roleSelect,
    st.sessionId && el('p', { class: 'note' },
      roleKnown
        ? 'Taken from the session the coach created. Change it only if this clip was actually '
          + 'filmed from a different camera position — the server checks it against the 041 vocabulary.'
        : 'The session could not be read back, so this is NOT the session’s own value — it is the '
          + 'first of the server’s four. Check it before uploading: the server refuses a clip whose '
          + 'camera role does not match, and it refuses it only after the whole file has uploaded.'),
    picker,
    chosen,
    status,
  ));

  out.append(await darkProbe());
  out.append(await inFlightPanel(rerender));
  mount(root, out);
}

/**
 * ASK THE SERVER WHETHER UPLOAD IS ACTUALLY AVAILABLE, AND SAY SO PLAINLY.
 *
 * This is the honest-DARK surface. It calls the real status endpoint with a throwaway key and reads
 * the answer:
 *
 *   · 503 `r2_not_configured`  → "video upload is not enabled yet", plus the exact variables missing
 *   · 503 `practice_lab_unavailable` → the lab is switched off, and by which switch
 *   · 404 `route_not_mounted`  → these routes are not in the running build at all
 *   · 401 / 403                → the surface exists and reachable; you are simply not this athlete's
 *   · anything else            → upload is live
 *
 * IT DOES NOT GUESS FROM A LOCAL FLAG. A client-side `UPLOAD_ENABLED` constant is the same defect
 * the backend's upload lane was written to end: a control whose value is decided somewhere other
 * than where the truth lives. The server is asked, every time this screen opens.
 */
async function darkProbe(): Promise<HTMLElement> {
  const st = getState();
  if (!st.athlete) return el('div');
  // A key that matches the server's CLIENT_UUID_RE but names itself as a probe, so it is obvious in
  // a log that no real capture is behind it. Status is a pure read — it creates nothing.
  const r = await uploadStatus(st.athlete.athleteId, 'probe-upload-availability-0000', {});

  if (isRefusal(r)) {
    if (r.kind === 'upload_not_configured' || r.kind === 'upload_misconfigured'
      || r.kind === 'lab_stopped' || r.kind === 'not_mounted') {
      return section('Video upload status', refusalView(r));
    }
    if (r.kind === 'unauthenticated' || r.kind === 'forbidden') {
      return section('Video upload status',
        el('p', { class: 'note' },
          'Storage availability could not be determined, because this token is not allowed to read '
          + 'this athlete’s uploads. That is an access answer, not a storage answer.'),
        refusalView(r));
    }
    return section('Video upload status', refusalView(r));
  }
  return section('Video upload status',
    el('p', { class: 'ok' }, 'Video storage is configured and reachable. Uploads will really upload.'));
}

/** Uploads currently in the queue for this athlete, showing DURABLY ACKNOWLEDGED progress only. */
async function inFlightPanel(rerender: () => void): Promise<HTMLElement> {
  const st = getState();
  const jobs = (await listJobs()).filter(
    (j): j is UploadJob => j.kind === 'upload' && j.athlete_id === st.athlete?.athleteId,
  );
  if (!jobs.length) return section('Clips queued for this athlete', empty('Nothing queued.'));

  return section('Clips queued for this athlete',
    el('p', { class: 'note' },
      'Progress below counts chunks storage has DURABLY ACKNOWLEDGED — not chunks this phone has '
      + 'pushed. The two differ exactly when the signal dies mid-chunk, which is the only moment the '
      + 'number matters. "Safe to delete locally" is the server’s own figure and is the only number '
      + 'you may free storage against.'),
    ...jobs.map((j) => el('div', { class: 'row-item' },
      el('p', { class: 'mono small' }, j.file_name),
      field('State', j.state),
      field('Size', fmt(j.total_bytes)),
      field('Chunks acknowledged', j.parts_total ? `${j.parts_acknowledged} of ${j.parts_total}` : 'not started'),
      field('Safe to delete locally',
        j.safe_to_delete_local_bytes === null
          ? 'nothing yet — keep this clip on the device'
          : fmt(j.safe_to_delete_local_bytes)),
      field('Attempts', j.attempts),
      j.replayed === true && el('p', { class: 'ok' },
        'The server recognised this as a replay and wrote nothing a second time.'),
      j.last_error && j.last_error_kind
        && refusalView(rehydrate(j), { onRetry: rerender }),
    )),
  );
}

/**
 * Rebuild a `Refusal` from what the queue stored, so a failed job renders through the SAME view as
 * a live one.
 *
 * Two renderers for one condition drift, and the one that drifts is always the one nobody is
 * looking at — here that would be the offline path, which is the path that matters most.
 */
function rehydrate(j: UploadJob): ReturnType<typeof classify> {
  const d = (j.last_error_detail ?? {}) as Record<string, unknown>;
  return classify(
    typeof d['status'] === 'number' ? d['status'] : 0,
    {
      message: j.last_error,
      reason: d['reason'] ?? null,
      switch_name: d['switch_name'] ?? null,
      missing: d['missing'] ?? [],
      note: d['note'] ?? null,
      ...(j.last_error_kind === 'lab_stopped' ? { error: 'practice_lab_unavailable' } : {}),
      ...(j.last_error_kind === 'not_mounted' ? { unmounted: true } : {}),
    },
    typeof d['request'] === 'string' ? d['request'] : 'queued upload',
  );
}
